import { EventApi, EventContentArg, MoreLinkArg } from '@fullcalendar/core';
import deLocale from '@fullcalendar/core/locales/de';
import enGbLocale from '@fullcalendar/core/locales/en-gb';
import esLocale from '@fullcalendar/core/locales/es';
import nlLocale from '@fullcalendar/core/locales/nl';
import InteractionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import TimeGridPlugin from '@fullcalendar/timegrid';
import { Locales } from '@griply/types';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import {
  CalendarSyncStates,
  FullCalendarEvent,
  ToggleCalendarVisibility,
} from 'shared/components/ui/full-calendar-event';
import { FullCalendarMorePopover } from 'shared/components/ui/full-calendar-more-popover';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { useResizeObserver } from 'shared/hooks/use-resize-observer';
import { eventAllow } from 'shared/lib/full-calendar/event-allow';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import {
  fullCalendarMilitaryTime,
  fullCalendarStandardTime,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { SmartList } from 'shared/types/smart-list';
import { Tag } from 'shared/types/tag';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './full-calendar-day.style';

const plugins = [TimeGridPlugin, InteractionPlugin];
const supportedLocales = [nlLocale, enGbLocale, esLocale, deLocale];
const localeMap: Record<Locales, string> = {
  [Locales.NlNL]: 'nl',
  [Locales.EnUS]: 'en-gb',
  [Locales.EsES]: 'es',
  [Locales.DeDE]: 'de',
};

export type FullCalendarDayProps = Omit<
  React.ComponentProps<typeof FullCalendar>,
  | 'plugins'
  | 'initialView'
  | 'dayHeaderContent'
  | 'eventAllow'
  | 'contentHeight'
> & {
  tasks: Task[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  smartLists: SmartList[];
  onCompleteTask: (id: ID, date: Timestamp) => void;
  onClickEvent?: (id: ID) => void;
  referenceDate?: Timestamp;
  fullCalendarRef?: React.RefObject<FullCalendar | null>;
  timeFormat: TimeFormatOptions;
  weekStartsOn: WeekDays;
  calendarSyncStates?: CalendarSyncStates;
  onToggleCalendarVisibility?: ToggleCalendarVisibility;
};

export const FullCalendarDay = forwardRef<HTMLDivElement, FullCalendarDayProps>(
  (
    {
      tasks,
      goals,
      lifeAreas,
      tags,
      smartLists,
      onCompleteTask,
      onClickEvent,
      referenceDate = new Date(),
      events,
      fullCalendarRef,
      timeFormat,
      weekStartsOn,
      calendarSyncStates,
      onToggleCalendarVisibility,
      ...rest
    },
    ref,
  ) => {
    const locale = useI18nLocale();
    const _fullCalendarRef = useForwardedRef(fullCalendarRef);
    const scrollContainerRef = useForwardedRef<HTMLDivElement>(ref);
    const [itemWidth, setItemWidth] = useState(120);
    const [moreInfo, setMoreInfo] = useState<{
      position: { top: number; left: number };
      events: EventApi[];
    }>();

    const onResize = useCallback(() => {
      _fullCalendarRef.current?.getApi().updateSize();
      setItemWidth((scrollContainerRef.current?.clientWidth ?? 7) / 7);
    }, [_fullCalendarRef, scrollContainerRef]);
    useResizeObserver(scrollContainerRef, onResize, 1);

    const onMore = useCallback(({ jsEvent, hiddenSegs }: MoreLinkArg) => {
      jsEvent.preventDefault();
      setMoreInfo({
        position: {
          top: (jsEvent as MouseEvent).clientY,
          left: (jsEvent as MouseEvent).clientX,
        },
        events: hiddenSegs.map(({ event }) => event),
      });

      return '';
    }, []);

    const closeMore = useCallback(() => setMoreInfo(undefined), []);

    const _onClickEvent = useCallback(
      (id: ID) => {
        onClickEvent?.(id);
        closeMore();
      },
      [closeMore, onClickEvent],
    );

    // Update the calendar view when referenceDate changes
    useEffect(() => {
      if (referenceDate && _fullCalendarRef.current) {
        queueMicrotask(() =>
          _fullCalendarRef.current?.getApi().gotoDate(referenceDate),
        );
      }
    }, [referenceDate, _fullCalendarRef]);

    const renderEvent = useCallback(
      ({ event }: EventContentArg) => (
        <FullCalendarEvent
          event={event}
          tasks={tasks}
          goals={goals}
          lifeAreas={lifeAreas}
          tags={tags}
          smartLists={smartLists}
          onComplete={onCompleteTask}
          onClick={_onClickEvent}
          calendarSyncStates={calendarSyncStates}
          onToggleCalendarVisibility={onToggleCalendarVisibility}
        />
      ),
      [
        tasks,
        goals,
        lifeAreas,
        tags,
        smartLists,
        onCompleteTask,
        _onClickEvent,
        calendarSyncStates,
        onToggleCalendarVisibility,
      ],
    );

    return (
      <Styled.ScrollContainer ref={scrollContainerRef}>
        <Styled.Container $itemWidth={itemWidth}>
          <FullCalendar
            ref={_fullCalendarRef}
            plugins={plugins}
            initialView="timeGridDay"
            slotDuration="00:15:00"
            slotLabelInterval="01:00:00"
            eventAllow={eventAllow}
            height="100%"
            locales={supportedLocales}
            locale={localeMap[locale]}
            nowIndicator
            stickyHeaderDates
            eventResizableFromStart
            dayMaxEvents={4}
            moreLinkClick={onMore}
            eventContent={renderEvent}
            slotLabelFormat={
              timeFormat === TimeFormatOptions.Standard
                ? fullCalendarStandardTime
                : fullCalendarMilitaryTime
            }
            events={events}
            allDaySlot
            {...rest}
          />

          {moreInfo && (
            <FullCalendarMorePopover
              events={moreInfo.events}
              location={moreInfo.position}
              onOutside={closeMore}
              weekStartsOn={weekStartsOn}
              tasks={tasks}
              goals={goals}
              lifeAreas={lifeAreas}
              onComplete={onCompleteTask}
              tags={tags}
              smartLists={smartLists}
              onClick={_onClickEvent}
              calendarSyncStates={calendarSyncStates}
              onToggleCalendarVisibility={onToggleCalendarVisibility}
            />
          )}
        </Styled.Container>
      </Styled.ScrollContainer>
    );
  },
);
